import { observer } from 'mobx-react-lite'
import React, { useCallback } from 'react'

import {
   OnValueChangeType,
   fieldValueProps,
   InputPropsVariant,
   InputPropsConfig,
   OnValueChangedType,
} from './field-props'
import FormErrorHandler from './FormErrorHandler'
import { StandardErrorProps } from './FormInputHandler'

/**
 * InputProps parameters
 */
interface InputPropsProps<
   T extends AnyObject,
   P extends Extract<keyof T, string>,
   A extends AnyObject
> {
   /**
    * the component that you want InputProps to control. InputProps will add the `value`
    * and `onChange` props to the component to automate the state update to
    * stateObject[propertyName]
    */
   children: React.ReactElement
   /**
    * object that holds the property that will be bound to this component. That property
    * has to be a mobx observable
    */
   stateObject: T
   /**
    * name of the property inside the stateObject that will be bound to this component.
    * That property has to be a mobx observable
    */
   propertyName: P
   /**
    * in case you want to be notified about when a change will take place. Your callback
    * can return a promise that, if fulfilled with a return of false, will preven the
    * update to happen.
    */
   onValueChange?: OnValueChangeType<T, P, A>
   /**
    * in case you want to be notified about when a change has happened
    */
   onValueChanged?: OnValueChangedType<T, P, A>
   /**
    * in case you are using a FormErrorHandler to handle the form errors
    */
   errorHandler?: FormErrorHandler<T>
   /**
    * some built-in variants
    */
   variant?: InputPropsVariant
   /**
    * some built-in configurations
    */
   config?: InputPropsConfig
   /** additinal data that will be passed to onValueChange and onValueChanged */
   additionalChangeData?: A
}

/**
 * A React component that provides a two-way data-binding feel to your forms controlled by a mobx state.
 * @param props InputProps props
 * @example
 *
 * const localStore = useLocalStore(() => ({
 *    myFieldValue: ''
 * }))
 *
 * <InputProps stateObject={localStore} propertyName='myFieldValue'>
 *    <input/>
 * </InputProps>
 *
 */
export default observer(function InputProps
   <T extends AnyObject, P extends Extract<keyof T, string>, A extends AnyObject>({
      config = {},
      ...props
   }: InputPropsProps<T, P, A>) {
   const [
      stateObject,
      propertyName,
      onValueChange,
      onValueChanged,
      isCheckboxProps,
      errorHandler,
      variant,
      obsConfig,
      additionalChangeData,
      value,
   ] = [
         props.stateObject,
         props.propertyName,
         props.onValueChange,
         props.onValueChanged,
         config.isCheckbox,
         props.errorHandler,
         props.variant,
         config,
         props.additionalChangeData,
         props.stateObject[props.propertyName],
         props.errorHandler &&
         props.errorHandler.errors &&
         props.errorHandler.getFieldError(props.propertyName), // this is for MobX to react to errorHandler changes to this field
         (props.stateObject[props.propertyName] as any)?.value, // this is for MobX to react to value changes
      ]

   const isCheckbox =
      isCheckboxProps === undefined
         ? typeof value === 'boolean'
         : isCheckboxProps

   const newFieldProps = fieldValueProps({
      store: stateObject,
      propertyName,
      onValueChange,
      variant,
      config: { ...obsConfig, isCheckbox },
      onValueChanged,
      paramsToValueChange: additionalChangeData
   })
   // eslint-disable-next-line react-hooks/exhaustive-deps
   const onChange = useCallback(newFieldProps.onChange, [stateObject])

   const errorProps: StandardErrorProps = {}

   const error = errorHandler?.getFieldError(propertyName)
   if (error) {
      errorProps.error = true
      errorProps.helperText = error
   }

   const newProps = isCheckbox
      ? { onChange, checked: newFieldProps.value, ...errorProps }
      : { onChange, value: newFieldProps.value, ...errorProps }

   return React.cloneElement(props.children, newProps)
}
)
